import { v4 as uuidv4 } from 'uuid';
import { AgentTypes, EventTypes } from '../constants/event';
import { DUMMY_RESTAURANT_CODE } from '../constants';
import {
  IssueReportContext,
  IssueReportEventData,
} from '../redux/features/issueReport/issueReport.props';
import { agent } from './agent';
import { Counter } from './counter';
import logger from './logger';
import { readEnvVariable } from '../utils';

/**
 * HITL-1492 Pillar 3, Part A — REST-primary transport for issue reports.
 *
 * REST-primary, not websocket, for two reasons that are both load-bearing:
 *
 * 1. `socketMiddleware` only sends when the socket is open and otherwise drops the
 *    action silently — there is no queue or retry anywhere in this codebase.
 * 2. `navigateToTaskRouterPage` dispatches closeConnection(), resetAI() and
 *    resetSelectedRestaurantDetails() before navigating, so at the exact moment a
 *    classification is submitted the socket is closed and store_code is gone.
 *
 * Lives outside the slice on purpose: the retry queue is mutable module state and the
 * flush needs a module-level `pagehide` listener, neither of which belongs in a
 * reducer. Same structural choice `src/services/index.ts` makes for New Relic logs.
 */

const MAX_ATTEMPTS = 4;
/** Delay before attempts 2, 3 and 4. Exhausted => the record is marked failed. */
const BACKOFF_MS = [500, 2000, 8000];
/** Cap on how many pending sends the pagehide flush will attempt. */
const FLUSH_MAX_ON_PAGEHIDE = 5;
/**
 * `fetch(..., { keepalive: true })` shares a 64 KB body budget across ALL in-flight
 * keepalive requests — a browser limit, not a server one. Staying well under it means
 * an oversized report still gets a chance rather than failing at exactly the moment
 * we are trying hardest to save it.
 */
const FLUSH_SIZE_BUDGET_BYTES = 48 * 1024;

/**
 * In Jest, keep the module inert: no background timers and no window listeners, so
 * nothing outlives a test run. The pre-push hook runs the full suite plus a
 * production build, and a stray handle hangs the push rather than failing a test.
 * Mirrors the same guard in src/services/index.ts.
 */
const isTestEnv = process.env.NODE_ENV === 'test';

/** Own seq stream, matching how every other event type counts independently. */
export const issueReportSeqIdCounter = new Counter();

export interface IssueReportEnvelope {
  id: string;
  seq: number;
  event: EventTypes;
  agent_type: AgentTypes;
  agent_id: string;
  session_id: string | null;
  lane_id?: string;
  restaurant_code: string;
  timestamp: string;
  environment: string;
  metadata: Record<string, unknown>;
  data: IssueReportEventData;
}

export interface IssueReportTransportConfig {
  eventAPI: string | null;
  eventAPIAuth: string | null;
}

export interface BuildEnvelopeArgs {
  data: IssueReportEventData;
  /**
   * Taken from the FROZEN context, never from live state — that is what makes a
   * post-teardown amend land with a resolvable session join.
   */
  sessionId: string | null;
  restaurantCode: string | null;
  laneId?: string | null;
  agentMetadata: Record<string, unknown>;
}

/**
 * Mirrors the envelope `messagingTransformMiddleware` builds — NOT
 * `sendAsrIncorrectEvent`'s, which omits `environment` and `lane_id`, nor
 * `sendNetworkCallLogs`'s, which buries `environment` inside `metadata`. Since the
 * websocket mirror's envelope IS built by that middleware, matching anything else
 * would ship two structurally different copies of the same report.
 */
export const buildIssueReportEnvelope = ({
  data,
  sessionId,
  restaurantCode,
  laneId,
  agentMetadata,
}: BuildEnvelopeArgs): IssueReportEnvelope => ({
  id: uuidv4(),
  seq: issueReportSeqIdCounter.increment(),
  event: EventTypes.issueReport,
  agent_type: AgentTypes.HITL,
  agent_id: agent.getAgentId(),
  session_id: sessionId,
  ...(laneId ? { lane_id: laneId } : null),
  restaurant_code: restaurantCode || DUMMY_RESTAURANT_CODE,
  timestamp: new Date().toISOString(),
  environment: readEnvVariable('DEPLOY_ENV') || 'unknown',
  metadata: agentMetadata,
  data,
});

interface PendingSend {
  key: string;
  /**
   * Built ONCE and reused across retries. Regenerating `id` or `seq` per attempt
   * would defeat envelope-level dedupe and inflate the seq stream.
   */
  envelope: IssueReportEnvelope;
  attempts: number;
  timer: ReturnType<typeof setTimeout> | null;
}

const queue = new Map<string, PendingSend>();

export interface DeliveryCallbacks {
  onSuccess: (issueId: string, revision: number) => void;
  onFailure: (issueId: string, revision: number) => void;
}

let config: IssueReportTransportConfig = {
  eventAPI: null,
  eventAPIAuth: null,
};
let callbacks: DeliveryCallbacks | null = null;

/**
 * Wire the transport to config and to the slice's delivery reducers. Called from the
 * slice's thunks so this module never imports the store.
 */
export const configureIssueReportTransport = (
  next: IssueReportTransportConfig,
  nextCallbacks: DeliveryCallbacks
) => {
  config = next;
  callbacks = nextCallbacks;
};

const keyOf = (issueId: string, revision: number) => `${issueId}:${revision}`;

const clearPending = (pending: PendingSend) => {
  if (pending.timer) {
    clearTimeout(pending.timer);
    pending.timer = null;
  }
  queue.delete(pending.key);
};

const post = async (
  envelope: IssueReportEnvelope,
  keepalive = false
): Promise<boolean> => {
  const { eventAPI, eventAPIAuth } = config;
  if (!eventAPI || !eventAPIAuth) {
    // Never return silently: a dropped report is the exact failure mode this whole
    // pillar exists to remove, so it is surfaced rather than swallowed.
    logger.warn({
      message:
        'Cannot deliver issue report: EVENT_API or EVENT_API_BASIC_AUTH_PARAM is not configured.',
      issueId: envelope.data.issue_id,
    });
    return false;
  }

  try {
    // Raw fetch, NOT the post() wrapper in utils/api.ts: apiCall dispatches
    // sendNetworkCallLogs on both success and failure, which would emit a second
    // event to this same backend for every report and double the ingest volume of
    // the one path that is already expected to run hot.
    const response = await fetch(`${eventAPI}/v1/event`, {
      method: 'POST',
      headers: {
        Authorization: `Basic ${eventAPIAuth}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(envelope),
      ...(keepalive ? { keepalive: true } : null),
    });
    return response.ok;
  } catch (error) {
    logger.warn({
      message: 'Issue report delivery failed',
      issueId: envelope.data.issue_id,
      revision: envelope.data.revision,
      error: String(error),
    });
    return false;
  }
};

const attempt = async (pending: PendingSend): Promise<void> => {
  pending.attempts += 1;
  const { issue_id: issueId, revision } = pending.envelope.data;

  const delivered = await post(pending.envelope);
  if (delivered) {
    clearPending(pending);
    callbacks?.onSuccess(issueId, revision);
    return;
  }

  if (pending.attempts >= MAX_ATTEMPTS) {
    logger.error({
      message: `Issue report ${issueId} rev ${revision} not delivered after ${MAX_ATTEMPTS} attempts`,
    });
    clearPending(pending);
    callbacks?.onFailure(issueId, revision);
    return;
  }

  const delay =
    BACKOFF_MS[pending.attempts - 1] ?? BACKOFF_MS[BACKOFF_MS.length - 1];
  if (isTestEnv) {
    // No background timer under Jest — tests drive retries via retryPendingNow().
    return;
  }
  pending.timer = setTimeout(() => {
    pending.timer = null;
    void attempt(pending);
  }, delay);
};

/** Queue an envelope and start delivering it. */
export const deliverIssueReportEnvelope = (envelope: IssueReportEnvelope) => {
  const key = keyOf(envelope.data.issue_id, envelope.data.revision);
  const existing = queue.get(key);
  if (existing) {
    // Same (issue_id, revision) already in flight — the consumer upserts on that
    // pair, so a second delivery would be a no-op anyway.
    return;
  }
  const pending: PendingSend = { key, envelope, attempts: 0, timer: null };
  queue.set(key, pending);
  void attempt(pending);
};

/** Test seam: run the next attempt for every queued send without waiting on timers. */
export const retryPendingNow = async () => {
  await Promise.all(
    Array.from(queue.values()).map((pending) => attempt(pending))
  );
};

/** Test seam. */
export const getPendingCount = () => queue.size;

/** Test seam — module state must not leak between tests. */
export const resetIssueReportTransport = () => {
  queue.forEach((pending) => {
    if (pending.timer) clearTimeout(pending.timer);
  });
  queue.clear();
  config = { eventAPI: null, eventAPIAuth: null };
  callbacks = null;
};

/**
 * Strip the two largest variable-length blocks so an oversized payload still fits the
 * keepalive budget. Marked so a consumer never mistakes a reduced context for a
 * session with an empty cart and no transcript.
 */
const reduceForFlush = (envelope: IssueReportEnvelope): IssueReportEnvelope => {
  const context = envelope.data.context;
  if (!context) return envelope;
  const reduced: IssueReportContext = {
    ...context,
    transcript: [],
    carts: { agent_cart: [], pos_cart: [], truncated: true },
    reduced_for_flush: true,
  };
  return { ...envelope, data: { ...envelope.data, context: reduced } };
};

/**
 * Best-effort flush when the tab is hidden or unloaded.
 *
 * `sendBeacon` is NOT usable here — it cannot set the Authorization header the event
 * API requires — so this is `fetch` with `keepalive`, capped and size-reduced.
 *
 * KNOWN GAP, conscious: a page RELOAD before this runs still loses queued sends.
 * Surviving that needs IndexedDB and is explicitly deferred; revisit if telemetry
 * shows real loss.
 */
export const flushIssueReportsOnPageHide = () => {
  const pending = Array.from(queue.values()).slice(-FLUSH_MAX_ON_PAGEHIDE);
  pending.forEach((entry) => {
    const serialized = JSON.stringify(entry.envelope);
    const envelope =
      serialized.length > FLUSH_SIZE_BUDGET_BYTES
        ? reduceForFlush(entry.envelope)
        : entry.envelope;
    void post(envelope, true);
  });
};

if (!isTestEnv && typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => flushIssueReportsOnPageHide());
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushIssueReportsOnPageHide();
    }
  });
}
